import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, List, Icon, Message } from "semantic-ui-react";
import styled from "styled-components";
import {
  verifyBusinessAddress,
  clearBusinessAddressSuggestions,
  updateBusinessAddressValidatedFlag,
} from "../../../actions/businessAddress";

// Canada Post address validation for BUSINESS/CORPORATE addresses. This is the
// business-flow counterpart of AddressValidationComponent (person/employee): it is
// scoped by `fieldKey` so a page with more than one business address (e.g. a
// Corporate Legal Address and a Mailing Address) validates each independently, and
// it reports its validated state back through `onValidationChange` so a class /
// redux-form parent can gate its "Next"/"Save" button without reading the slice.

// The single switch for business address validation across every business
// enrolment flow: self-enrolment, company enrolment (quick/full), brokerage house
// and TPA. It is the only thing the twenty-odd mount points across sixteen flow
// files consult, so flipping it here is the whole change either way.
//
// While on, each flow's "Save And Continue"/finalise button waits for the address
// to be validated and the review pages list any address that is not.
//
// While off, this component renders nothing and reports itself as validated, so
// those gates open on mount and the "address not validated" review entries drop
// off. It was off for a spell because the Canada Post account had run out of
// credit: the lookup then returns an account-level error rather than suggestions,
// which left every enroller stuck on "Click Validate Address above to continue"
// with no way forward. The account has since been topped up.
//
// Note that a hand-typed address almost never satisfies the backend's
// `isInputAddressValid`, which is an exact string equality against Canada Post's
// formatted output ("549 Charnwood Court ... L1V 4Y5" vs "549 Charnwood Crt ...
// L1V4Y5"). That is expected: the flow then opens the suggestions modal and the
// enroller picks the match, which validates. Only an empty suggestion list is a
// dead end.
//
// Employee/person address validation is a separate component and is unaffected.
export const IS_BUSINESS_ADDRESS_VALIDATION_ENABLED = true;

interface AddressSuggestion {
  street1: string;
  street2: string;
  city: string;
  province: string;
  postalCode: string;
  country: string;
  completeAddressString: string;
}

interface BusinessAddressValidationProps {
  // Unique per address input on the page (e.g. "selfEnrolment.corporate").
  fieldKey: string;
  address: {
    streetOne: string;
    streetTwo?: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  };
  onAddressSelect: (address: {
    streetOne: string;
    streetTwo: string;
    city: string;
    province: string;
    postalCode: string;
    country: string;
  }) => void;
  // Fired whenever the validated flag flips, so parents can gate their submit.
  onValidationChange?: (isValidated: boolean) => void;
  disabled?: boolean;
}

// A normalized key of the mutable address fields. Used to tell whether the address
// currently in the form is still the one we last validated, so a validation isn't
// dropped just because selecting a suggestion updates the fields.
const addressKey = (a: {
  streetOne?: string;
  streetTwo?: string;
  city?: string;
  province?: string;
  postalCode?: string;
}): string =>
  [a.streetOne, a.streetTwo, a.city, a.province, a.postalCode]
    .map((v) => (v || "").trim().toLowerCase())
    .join("|");

// The address-book DB stores country as an ISO alpha-2 code (e.g. "CA"). Canada
// Post suggestions (and some saved records) carry the full name ("Canada"), which
// the insert rejects with "Invalid @countryCodeAlpha (SQLSTATE 50000)". Normalise
// known names to codes so a validated address is always submittable; anything
// already in code form passes through unchanged.
const COUNTRY_NAME_TO_CODE: { [name: string]: string } = {
  canada: "CA",
  "united states": "US",
  "united states of america": "US",
  usa: "US",
};
const normalizeCountry = (c?: string): string => {
  const v = (c || "").trim();
  return COUNTRY_NAME_TO_CODE[v.toLowerCase()] || v;
};

const Container = styled.div`
  margin-top: 1rem;
  margin-bottom: 0;
`;

const ValidateButton = styled(Button)`
  font-family: Montserrat !important;
  background-color: #16a34a !important;
  color: white !important;
  width: 100%;
  display: flex !important;
  align-items: center;
  justify-content: center;
  font-weight: 600 !important;
  letter-spacing: 0.2px;
  border-radius: 10px !important;
  cursor: pointer !important;

  &:hover {
    background-color: #15803d !important;
  }

  &.disabled {
    opacity: 0.6 !important;
  }
`;

const ValidatedContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background-color: #f9fafb;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
`;

const ValidatedContent = styled.div`
  display: flex;
  align-items: center;
`;

const ValidatedText = styled.span`
  color: #16a34a;
  font-size: 14px;
  font-family: Montserrat;
  font-weight: 600;
  margin-left: 8px;
`;

const RevalidateButton = styled.button`
  color: #16a34a;
  font-size: 13px;
  font-family: Montserrat;
  font-weight: 600;
  text-decoration: underline;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  &:hover {
    color: #15803d;
  }
`;

const ErrorContainer = styled(Message)`
  &&& {
    font-family: Montserrat;
    margin-top: 12px;
  }
`;

const AddressSuggestionItem = styled(List.Item)`
  cursor: pointer;
  padding: 1rem !important;
  font-family: Montserrat !important;

  &:hover {
    background-color: #f3f4f6 !important;
  }
`;

// Prompt shown while the address is not yet validated, so the user knows the
// green "Validate Address" button is required before the (disabled) continue
// button will unlock.
const ValidateHint = styled.div`
  margin-top: 8px;
  font-family: Montserrat;
  font-size: 12.5px;
  font-weight: 600;
  color: #b45309;
  text-align: center;
`;

const BusinessAddressValidation: React.FC<BusinessAddressValidationProps> = ({
  fieldKey,
  address,
  onAddressSelect,
  onValidationChange,
  disabled = false,
}) => {
  const dispatch = useDispatch();
  const [showSuggestionsModal, setShowSuggestionsModal] = useState(false);
  const hasDispatchedValidation = useRef(false);

  const [
    addressSuggestions,
    isVerifyingAddress,
    validationError,
    isValidated,
    validatedKey,
  ] = useSelector((state: any) => [
    state.businessAddressReducer?.[fieldKey]?.responsePayload,
    state.businessAddressReducer?.[fieldKey]?.loading,
    state.businessAddressReducer?.[fieldKey]?.errorPayload,
    state.businessAddressReducer?.[fieldKey]?.isValidated,
    state.businessAddressReducer?.[fieldKey]?.validatedKey,
  ]);

  // "Validated" for display + gating = it was validated AND the address currently
  // on the page still matches what we validated. Because the validated key lives in
  // redux (keyed by fieldKey), this carries across wizard pages that share the same
  // fieldKey - so validating on page 1 shows "Address Validated" on later pages
  // without re-asking - and it drops automatically only when the address is edited.
  // While validation is switched off every address counts as validated, so the
  // parent gates open on mount and stay open however the address is edited.
  const currentlyValidated = IS_BUSINESS_ADDRESS_VALIDATION_ENABLED
    ? Boolean(isValidated && validatedKey && addressKey(address) === validatedKey)
    : true;

  useEffect(() => {
    // Clear on mount to prevent stale data
    dispatch(clearBusinessAddressSuggestions({ fieldKey }));
    hasDispatchedValidation.current = false;

    return () => {
      // Clear on unmount
      dispatch(clearBusinessAddressSuggestions({ fieldKey }));
      hasDispatchedValidation.current = false;
    };
  }, []);

  // Keep the parent's gate in sync with the (address-aware) validated state.
  useEffect(() => {
    if (onValidationChange) {
      onValidationChange(currentlyValidated);
    }
  }, [currentlyValidated]);

  useEffect(() => {
    // Reset the ref when the suggestions payload is cleared
    if (!addressSuggestions) {
      hasDispatchedValidation.current = false;
    }
  }, [addressSuggestions]);

  const handleValidate = () => {
    if (disabled) return;

    hasDispatchedValidation.current = false;

    const addressParts = [
      address.streetTwo,
      address.streetOne,
      address.city,
      address.province,
      address.postalCode,
      address.country === "CA" ? "Canada" : address.country,
    ].filter(Boolean);

    const searchString = addressParts.join(" ");
    dispatch(verifyBusinessAddress({ fieldKey, searchString }));
  };

  useEffect(() => {
    if (
      addressSuggestions &&
      !isVerifyingAddress &&
      !hasDispatchedValidation.current
    ) {
      const { isInputAddressValid, addressSuggestions: suggestions } =
        addressSuggestions;

      if (isInputAddressValid === true) {
        hasDispatchedValidation.current = true;
        // Correct a full-name country (e.g. "Canada") to its ISO code so the
        // address-book insert at submit doesn't reject it. address country is not
        // part of addressKey, so this doesn't disturb the validated state.
        const normalizedCountry = normalizeCountry(address.country);
        if (normalizedCountry !== address.country) {
          onAddressSelect({
            streetOne: address.streetOne,
            streetTwo: address.streetTwo ?? "",
            city: address.city,
            province: address.province,
            postalCode: address.postalCode,
            country: normalizedCountry,
          });
        }
        dispatch(
          updateBusinessAddressValidatedFlag({
            fieldKey,
            isValidated: true,
            validatedKey: addressKey(address),
          })
        );
      } else if (
        isInputAddressValid === false &&
        suggestions &&
        suggestions.length > 0
      ) {
        if (!currentlyValidated) {
          setShowSuggestionsModal(true);
        }
      }
    }
  }, [addressSuggestions, isVerifyingAddress]);

  const handleSuggestionSelect = (suggestion: AddressSuggestion) => {
    hasDispatchedValidation.current = true;

    const selectedAddress = {
      streetOne: suggestion.street1,
      streetTwo: suggestion.street2,
      city: suggestion.city,
      province: suggestion.province,
      postalCode: suggestion.postalCode,
      country: normalizeCountry(suggestion.country),
    };

    onAddressSelect(selectedAddress);

    dispatch(
      updateBusinessAddressValidatedFlag({
        fieldKey,
        isValidated: true,
        validatedKey: addressKey(selectedAddress),
      })
    );
    setShowSuggestionsModal(false);
    dispatch(clearBusinessAddressSuggestions({ fieldKey }));
  };

  const handleRevalidate = () => {
    hasDispatchedValidation.current = false;
    dispatch(
      updateBusinessAddressValidatedFlag({
        fieldKey,
        isValidated: false,
        validatedKey: null,
      })
    );
    dispatch(clearBusinessAddressSuggestions({ fieldKey }));
    handleValidate();
  };

  // No explicit "reset on edit" effect is needed: `currentlyValidated` is derived
  // from comparing the current address to the validated key, so editing any field
  // automatically makes it false (and the parent gate re-blocks) without a race.

  const suggestions = addressSuggestions?.addressSuggestions || [];

  // Nothing to show while it is switched off: no Validate Address control, no
  // validated badge, no suggestions modal. The hooks above still run, which is
  // what keeps the parent's gate open.
  if (!IS_BUSINESS_ADDRESS_VALIDATION_ENABLED) {
    return null;
  }

  return (
    <Container>
      {!currentlyValidated ? (
        <ValidateButton
          onClick={handleValidate}
          loading={isVerifyingAddress}
          disabled={
            disabled ||
            isVerifyingAddress ||
            !address.streetOne ||
            !address.city ||
            !address.province
          }
          className={isVerifyingAddress ? "disabled" : ""}
          type="button"
        >
          {!isVerifyingAddress && (
            <Icon name="checkmark" style={{ marginRight: "6px" }} />
          )}
          {isVerifyingAddress ? "Validating..." : "Validate Address"}
        </ValidateButton>
      ) : (
        <ValidatedContainer>
          <ValidatedContent>
            <Icon
              name="check circle"
              size="large"
              style={{ color: "#16a34a", margin: 0 }}
            />
            <ValidatedText>Address Validated</ValidatedText>
          </ValidatedContent>
          <RevalidateButton onClick={handleRevalidate} type="button">
            Re-validate
          </RevalidateButton>
        </ValidatedContainer>
      )}

      {!currentlyValidated && (
        <ValidateHint>
          Click "Validate Address" above to continue.
        </ValidateHint>
      )}

      {validationError && (
        <ErrorContainer negative>
          <Icon name="exclamation circle" />
          <Message.Content>
            <Message.Header>Validation Error</Message.Header>
            <p>{validationError}</p>
          </Message.Content>
        </ErrorContainer>
      )}

      <Modal
        open={showSuggestionsModal}
        onClose={() => setShowSuggestionsModal(false)}
        size="small"
      >
        <Modal.Header style={{ fontFamily: "Montserrat" }}>
          Select Validated Address
        </Modal.Header>
        <Modal.Content>
          <p style={{ fontFamily: "Montserrat", marginBottom: "1rem" }}>
            The address entered could not be validated. Please select from the
            suggestions below:
          </p>
          <List divided selection>
            {suggestions.map((suggestion: AddressSuggestion, index: number) => (
              <AddressSuggestionItem
                key={index}
                onClick={() => handleSuggestionSelect(suggestion)}
              >
                <List.Content>
                  <List.Header
                    style={{ fontFamily: "Montserrat", marginBottom: "0.5rem" }}
                  >
                    {suggestion.completeAddressString}
                  </List.Header>
                  <List.Description
                    style={{
                      fontFamily: "Montserrat",
                      fontSize: "12px",
                      color: "#6b7280",
                    }}
                  >
                    {suggestion.street1}
                    {suggestion.street2 ? `, ${suggestion.street2}` : ""},{" "}
                    {suggestion.city}, {suggestion.province}{" "}
                    {suggestion.postalCode}
                  </List.Description>
                </List.Content>
              </AddressSuggestionItem>
            ))}
          </List>
        </Modal.Content>
        <Modal.Actions>
          <Button
            onClick={() => setShowSuggestionsModal(false)}
            style={{ fontFamily: "Montserrat" }}
          >
            Cancel
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
};

export default BusinessAddressValidation;
